import type { PersonaOpportunityDisplayRow } from "../../../lib/persona-opportunity-row-model.js";
import type { PersonaScriptVariantMeta } from "../../../lib/persona-script-meta.js";
import type {
  PersonaOpportunitySet,
  PersonaOpportunitySetType,
} from "../../../types/persona-opportunities.types.js";

/** Always-on hub tabs — Sets is a peer, not a separate page. */
export type OpportunityHubTabKey =
  | "sets"
  | "opportunities"
  | "scripts"
  | "sent_to_vendor";

export const OPPORTUNITY_HUB_TAB_KEYS: OpportunityHubTabKey[] = [
  "sets",
  "opportunities",
  "scripts",
  "sent_to_vendor",
];

export type OpportunityHubViewMode = "cards" | "table";

export type OpportunityHubSetStatusFilter = "all" | "READY" | "GENERATING" | "FAILED";

export type OpportunityHubSetTypeFilter = "all" | PersonaOpportunitySetType;

export type OpportunityHubGroupBy = "persona" | "angle" | "set" | "type" | "feature";

export interface OpportunityHubSelectionMix {
  hasPersona: boolean;
  hasCompetitor: boolean;
}

export interface OpportunityHubTabCounts {
  sets: number;
  opportunities: number;
  scripts: number;
  sent_to_vendor: number;
}

export type OpportunityHubViews = Record<OpportunityHubTabKey, OpportunityHubViewMode>;

export const DEFAULT_OPPORTUNITY_HUB_VIEWS: OpportunityHubViews = {
  sets: "cards",
  opportunities: "table",
  scripts: "table",
  sent_to_vendor: "table",
};

/** List row plus the sent-to-production total from the sets list. */
export type HubChromeSet = PersonaOpportunitySet & { sentCount: number };

/** Loaded sets by id, so group headers can describe the set they belong to. */
export type HubSetsById = ReadonlyMap<string, HubChromeSet>;

/**
 * Hub rows carry their own set identity: the hub lists several sets at once, so
 * there is no selected set to read the name or type from.
 */
export interface HubChromeOpportunityRow extends PersonaOpportunityDisplayRow {
  setId: string;
  setName: string;
  setType: HubChromeSet["type"];
  /** Script bucket created for imports — not a generated persona angle. */
  isCustom: boolean;
  personaName: string | null;
  personaAvatarUrl: string | null;
  /** COMPETITOR-driven columns; unset on persona rows. */
  topicTitle?: string;
  topicDescription?: string;
  findings?: string;
  suggestion?: string;
  action?: string;
  dataset?: string;
  feature?: string;
  subFeature?: string;
  spendUsd?: number;
  benchmarkSpendUsd?: number;
  competitorSpendUsd?: number;
  /** Per-angle spend share. Legacy table shows this as the primary percent. */
  spendPerAngle?: number | null;
  benchmarkSpendPerAngle?: number | null;
  competitorSpendPerAngle?: number | null;
  kpi?: number;
  competitorKpi?: number;
  benchmarkKpi?: number;
  engine?: string;
  industry?: string;
  /** Legacy active/rejected state. Null displays as active. */
  legacyState?: "active" | "rejected" | null;
  /** ISO timestamp of the set's generation completion — the legacy Run date. */
  runDate?: string | null;
}

export interface HubChromeScriptRow {
  id: string;
  setId: string;
  setName: string;
  opportunityId: string;
  opportunityAngle: string;
  title: string;
  kind: "script" | "brief";
  sent: boolean;
  /** Who it went to and when; absent when unsent or the send details are unknown. */
  sentTo?: HubChromeScriptRowSentTo | null;
  isFavorite: boolean;
  /** Null or absent is Meta. */
  youtubeFormat?: "in_stream_16_9" | "shorts_9_16" | null;
  setType: PersonaOpportunitySetType;
  slotIndex: number;
  /** The script a hook variant was cut from; null for briefs and root scripts. */
  parentScriptId?: string | null;
  /** Labels a hook variant `S{parent}·H{n}`; set once the whole group is known. */
  variantMeta?: PersonaScriptVariantMeta | null;
  /** Script concept line; null for briefs. */
  concept: string | null;
  duration: string | null;
  /** Scenes in the latest scene version; null for briefs. */
  sceneCount: number | null;
  /** Brief hook package size; null for scripts. */
  hookPackage: number | null;
  /** Hooks at the script's latest version; null for briefs. */
  hookCount?: number | null;
  /** Latest version number of the script or brief. */
  versionCount: number;
  /** ISO timestamp. */
  createdAt: string;
}

export interface HubChromeScriptRowSentTo {
  vendorName: string;
  /** ISO timestamp. */
  sentAt: string;
  sentByUserName: string | null;
}

/** A script or brief of a group that has been sent to a vendor. */
export interface HubChromeScriptGroupSentItem {
  id: string;
  /** Null until the group's rows have loaded. */
  title: string | null;
  vendorName: string | null;
}

/** One opportunity on Scripts & Briefs. `rows` stays empty until the group is expanded. */
export interface HubChromeScriptGroup {
  /** The opportunity id. */
  key: string;
  label: string;
  personaTitle: string | null;
  isCustom: boolean;
  setId: string;
  setName: string;
  scriptCount: number;
  briefCount: number;
  /** Scripts by slot, then briefs by slot. */
  rows: HubChromeScriptRow[];
  isLoading: boolean;
  /** Known even while collapsed; titles fill in once the rows load. */
  sentItems: HubChromeScriptGroupSentItem[];
  /** True while the rows that name `sentItems` are loading. */
  isLoadingSentTitles: boolean;
}

/** Opportunity headers plus the rows of the groups the user has expanded. */
export interface HubScriptWindow {
  groups: HubChromeScriptGroup[];
  expandedKeys: ReadonlySet<string>;
  onToggleGroup: (groupKey: string) => void;
  /** Loads a collapsed group's rows so its sent badge can name what was sent. */
  onPeekGroup?: (groupKey: string) => void;
}

export type HubChromeOrderStatus = "sent" | "in_production" | "delivered";

export interface HubChromeOrderRow {
  id: string;
  setId: string;
  setName: string;
  opportunityId: string;
  scriptId: string;
  title: string;
  vendor: string;
  status: HubChromeOrderStatus;
}
